import type { RequestOptions } from '#imports';

export function useHourRegistrationAPI() {
  const { errors, request } = useRequest();
  const { t } = useEkI18n();
  const { openToast } = useUserMessages();

  async function getFitterHourConfiguration() {
    const url = versionedUrl('FitterHour/FitterHourConfiguration');

    return await request<FitterHourConfigurationResponse>(url);
  }

  async function createMultipleHourRegistrations(requestBody: FitterHourCreateRequest) {
    const url = versionedUrl('FitterHour/CreateMultiple');

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    const response = await request<FitterHourCreateResponse[]>(url, requestOptions);

    if (response && response?.HasErrors === false) {
      openToast(t('hourRegistration.alert.wasCreated'), ToastType.SUCCESS);
    }

    return response;
  }

  async function updateHourRegistration(requestBody: FitterHourUpdateRequest) {
    const url = versionedUrl('FitterHour/Update');

    const requestOptions: RequestOptions = {
      method: 'post',
      body: [requestBody]
    };

    const response = await request<FitterHourUpdateResponse[]>(url, requestOptions);

    if (response?.HasErrors === false) {
      openToast(t('hourRegistration.alert.wasChanged'), ToastType.SUCCESS);
    }

    return response;
  }

  async function deleteHourRegistration(requestBody: FitterHourDeleteRequest) {
    const url = versionedUrl('FitterHour/Delete');

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    const response = await request<FitterHourDeleteResponse>(url, requestOptions);

    if (response?.HasErrors === false && response?.Data.IsDeleted === true) {
      openToast(t('hourRegistration.alert.wasDeleted'), ToastType.SUCCESS);
    }

    return response;
  }

  async function getHourRegistrationsRange(requestBody: GetFitterHoursRequest) {
    const url = versionedUrl('FitterHour/HourRegistrationsRange');
    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<AppFitterHourContainerDTO>(url, requestOptions);
  }

  async function getFitterCategoriesForFitter(fitterId: number) {
    const url = versionedUrl('FitterHour/GetFitterCategoriesForFitter') + '?fitterId=' + fitterId;

    return await request<AppFitterCategoriesContainerDTO>(url);
  }

  async function getFitterCategoryDefaults(fitterId: number) {
    const url = versionedUrl('FitterHour/GetFitterCategoryDefaults') + '?fitterId=' + fitterId;

    return await request<FitterCategoryDefault[]>(url);
  }

  async function getTimeRegistrationsForWorksheetOverview(projectID: number, worksheetID: number, fitterIds?: number[]) {
    const url = versionedUrl('FitterHour/GetTimeRegistrationsForWorksheetOverview');

    const requestOptions: RequestOptions = {
      method: 'post',
      body: {
        projectID: projectID,
        worksheetID: worksheetID,
        fitterIds: fitterIds
      }
    };

    return await request<AppFitterHourWorksheetOverviewDTO>(url, requestOptions);
  }

  async function getFitterCategory(id: number) {
    const url = versionedUrl('FitterHour/GetFitterCategoryById') + '?id=' + id;

    return await request<AppFitterCategoryDTO>(url);
  }

  async function validateFitterHours(datesToCheck: string[], fitterID: number) {
    const url = versionedUrl('FitterHour/ValidateFitterHours');
    const requestBody: SalaryPeriodDateCheckRequest[] = datesToCheck.map(date => ({
      DateToCheck: date,
      FitterId: fitterID
    }));

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<FitterHourValidationResult>(url, requestOptions);
  }

  return {
    errors,
    getFitterHourConfiguration,
    getTimeRegistrationsForWorksheetOverview,
    createMultipleHourRegistrations,
    updateHourRegistration,
    deleteHourRegistration,
    getHourRegistrationsRange,
    getFitterCategoriesForFitter,
    getFitterCategoryDefaults,
    getFitterCategory,
    validateFitterHours
  };
}
